import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFish } from '@fortawesome/free-solid-svg-icons';
import { getAvailableSwimlaneOptions, getSwimlaneOption } from '../config/swimlaneConfig';
import { calculateDropdownPosition, closeAllDropdowns, CLOSE_ALL_DROPDOWNS_EVENT, FLOWCHART_PAN_START_EVENT } from '../shared/utils/dropdownPositioning';

// ═══════════════════════════════════════════════════════════════
// ICONS
// ═══════════════════════════════════════════════════════════════

// Fish icon for swimlanes 🐟
const SwimlanesIcon = () => (
  <FontAwesomeIcon icon={faFish} style={{ width: 14, height: 14 }} />
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/**
 * SwimlanesDropdown Component
 * 
 * Dropdown for selecting how to group Kanban cards into swimlanes.
 * Shows available grouping options based on current context.
 */
const SwimlanesDropdown = ({
  currentSwimlane = 'none',
  onSwimlaneChange,
  context = {}, // { allTags, nestedSprints, hasPriorityField, etc. }
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Get available options based on context
  const availableOptions = getAvailableSwimlaneOptions(context);
  const currentOption = getSwimlaneOption(currentSwimlane);

  // Calculate dropdown position with edge detection
  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition(calculateDropdownPosition(rect, 280));
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleSelect = (optionId, e) => {
    e?.stopPropagation();
    e?.preventDefault();
    onSwimlaneChange?.(optionId);
    setIsOpen(false);
  };

  const handleButtonClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!isOpen) {
      closeAllDropdowns(buttonRef.current); // Close any other open dropdowns first
      updatePosition();
    }
    setIsOpen(!isOpen);
  };

  // Listen for global close events (when another dropdown opens or view zooms/pans)
  useEffect(() => {
    const handleCloseAll = () => setIsOpen(false);
    window.addEventListener(CLOSE_ALL_DROPDOWNS_EVENT, handleCloseAll);
    window.addEventListener(FLOWCHART_PAN_START_EVENT, handleCloseAll);
    return () => {
      window.removeEventListener(CLOSE_ALL_DROPDOWNS_EVENT, handleCloseAll);
      window.removeEventListener(FLOWCHART_PAN_START_EVENT, handleCloseAll);
    };
  }, []);

  const isActive = currentSwimlane !== 'none';

  return (
    <>
      <button
        ref={buttonRef}
        className={`toolbar-btn ${isActive ? 'toolbar-btn--active' : ''}`}
        onClick={handleButtonClick}
        onMouseDown={(e) => e.stopPropagation()}
        title="Group items into swimlanes"
      >
        <span className="toolbar-btn__spacer" />
        <span className="toolbar-btn__content">
          <span className="toolbar-btn__icon">
            <SwimlanesIcon />
          </span>
          <span className="toolbar-btn__label">
            {isActive ? `Grouping: ${currentOption.label}` : 'Grouping'}
          </span>
        </span>
        <span className={`toolbar-btn__chevron ${isOpen ? 'toolbar-btn__chevron--open' : ''}`}>
          <ChevronIcon />
        </span>
      </button>

      {isOpen && dropdownPosition && createPortal(
        <div
          ref={dropdownRef}
          className="swimlanes-dropdown__menu"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            maxHeight: dropdownPosition.maxHeight,
            overflowY: 'auto',
            zIndex: 50000,
          }}
        >
          <div className="swimlanes-dropdown__hint">
            Organize items into horizontal swimlanes
          </div>
          <div className="swimlanes-dropdown__header">
            Group By
          </div>
          {availableOptions.map(option => (
            <button
              key={option.id}
              className={`swimlanes-dropdown__item ${currentSwimlane === option.id ? 'swimlanes-dropdown__item--selected' : ''}`}
              onClick={(e) => handleSelect(option.id, e)}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <span className="swimlanes-dropdown__item-check">
                {currentSwimlane === option.id && <CheckIcon />}
              </span>
              <span className="swimlanes-dropdown__item-content">
                <span className="swimlanes-dropdown__item-label">{option.label}</span>
                {option.description && (
                  <span className="swimlanes-dropdown__item-description">{option.description}</span>
                )}
              </span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

export default SwimlanesDropdown;

